import _debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';

export interface UseDebouncedFilterOptions {
  /**
   * The committed value the table is currently filtering by.
   */
  globalFilter?: string;
  /**
   * Seed value for the buffered (pre-debounce) filter.
   */
  initialFilter?: string;
  /**
   * Notified with each committed filter change.
   */
  onFilterChange?: (column: string, value: string) => void;
  /**
   * Resets to the first page when the committed filter changes.
   */
  onPageChange: (page: number) => void;
  /**
   * The table's current page index.
   */
  pageIndex: number;
  /**
   * Commits a filter value to the table.
   */
  setGlobalFilter: (value: string) => void;
}

/**
 * Buffers the filter input and commits it on a 400ms debounce, exposing the
 * un-debounced value for the controlled filter field.
 */
export const useDebouncedFilter = ({
  globalFilter,
  initialFilter,
  onFilterChange,
  onPageChange,
  pageIndex,
  setGlobalFilter,
}: UseDebouncedFilterOptions) => {
  const [currentFilter, setCurrentFilter] = useState(initialFilter);

  const updateFilter = useCallback(
    _debounce((column, value) => {
      if (value !== globalFilter) {
        if (onFilterChange) onFilterChange(column, value);
        setGlobalFilter(value);
        if (pageIndex > 0) onPageChange(0);
      }
    }, 400),
    [globalFilter, onFilterChange, pageIndex],
  );

  const onFilteredChange = useCallback(
    (column, value) => {
      if (value !== currentFilter) {
        setCurrentFilter(value);
        updateFilter(column, value);
      }
    },
    [currentFilter, updateFilter],
  );

  return { currentFilter, onFilteredChange };
};

export default useDebouncedFilter;
